import React from "react";
import { interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";

// ─────────────────────────────────────────────────────────────────────────────
// Shared CTA kit for Factory Decoder.  Transparent background — every CTA is an
// element that springs IN over the footage, holds, then springs OUT.  No full
// undercards; the only "card" allowed is the button/pill that IS the CTA.
// English copy. 1280×704 @ 24fps.  All sizes in real px (scale at composite).
// ─────────────────────────────────────────────────────────────────────────────

export const YT_RED = "#FF0033";
export const INK = "#0c0c0e";
export const PAPER = "#ffffff";
export const LIKE_BLUE = "#2563eb";

// shared bell swing — the exact damped pendulum used by the Subscribe-bell CTA.
// Starts at `startSec`, swings, settles to 0 over ~1.6s. Reuse everywhere a bell appears.
export function bellRing(frame: number, fps: number, startSec = 0.6): number {
  const t = frame - Math.round(fps * startSec);
  if (t <= 0) return 0;
  return Math.sin(t * 0.9) * interpolate(t, [0, fps * 1.6], [16, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
}

// pop-in / hold / pop-out envelope. Returns {o:opacity, s:scale, y:slideY}
export function useEnvelope(opts?: { inEnd?: number; outStart?: number; total?: number }) {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const total = opts?.total ?? durationInFrames;
  const inEnd = opts?.inEnd ?? Math.round(fps * 0.55);
  const outStart = opts?.outStart ?? total - Math.round(fps * 0.5);

  const sIn = spring({ fps, frame, config: { damping: 13, mass: 0.7 } });
  const oOut = interpolate(frame, [outStart, total], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const sOut = interpolate(frame, [outStart, total], [1, 0.86], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const oIn = interpolate(frame, [0, inEnd], [0, 1], { extrapolateRight: "clamp" });

  return {
    o: Math.min(oIn, oOut),
    s: (0.7 + 0.3 * sIn) * sOut,
    y: interpolate(sIn, [0, 1], [26, 0]),
    frame,
    fps,
    total,
  };
}

// soft drop shadow so white text survives any footage
export const TEXT_SHADOW = "0 2px 10px rgba(0,0,0,0.55), 0 0 2px rgba(0,0,0,0.7)";
export const BTN_SHADOW = "0 10px 28px rgba(0,0,0,0.45)";

// ── inline SVG icons (no external assets) ────────────────────────────────────
export const BellIcon: React.FC<{ size?: number; color?: string; ring?: number }> = ({ size = 26, color = "#fff", ring = 0 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ transform: `rotate(${ring}deg)`, transformOrigin: "50% 10%" }}>
    <path d="M12 3a6 6 0 0 0-6 6c0 4-1.5 5.5-2 6h16c-.5-.5-2-2-2-6a6 6 0 0 0-6-6Z" fill={color} />
    <path d="M10 19a2 2 0 0 0 4 0" stroke={color} strokeWidth="1.8" strokeLinecap="round" fill="none" />
    <circle cx="18" cy="5" r="3" fill="#FFC400" />
  </svg>
);

export const ThumbIcon: React.FC<{ size?: number; color?: string }> = ({ size = 26, color = "#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M7 10v10H4V10h3Zm3 0 3-6c1.3 0 2.2 1.1 2 2.4L17.3 10H20a2 2 0 0 1 2 2.3l-1 6a2 2 0 0 1-2 1.7h-9V10Z" fill={color} />
  </svg>
);

export const ShareIcon: React.FC<{ size?: number; color?: string }> = ({ size = 26, color = "#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M14 9V5l8 7-8 7v-4C8 12 5 14 3 18c0-7 4-9 11-9Z" fill={color} />
  </svg>
);

export const PlayIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill={YT_RED} />
    {/* triangle 15 units tall in the 24 viewBox, centered */}
    <path d="M7.5 4.5 19.5 12 7.5 19.5V4.5Z" fill="#fff" />
  </svg>
);

// mouse pointer used to show a "click" landing on a button
export const CursorIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M5 3l14 7-6 1.5L10 19 5 3Z" fill="#fff" stroke="#111" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
